//src/components/common/Input.tsx
import * as React from "react";

function cn(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(" ");
}

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {}

// File input wrapper for best UX
const FileInput = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, ...props }, ref) => {
    const [file, setFile] = React.useState<File | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    React.useImperativeHandle(ref, () => inputRef.current!);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newFile = e.target.files?.[0] || null;
      setFile(newFile);
      props.onChange?.(e);
    };

    const handleRemove = (e: React.MouseEvent) => {
      e.preventDefault();
      setFile(null);
      if (inputRef.current) {
        inputRef.current.value = "";
        const event = new Event("change", { bubbles: true });
        inputRef.current.dispatchEvent(event);
      }
    };

    return (
      <div className="flex items-center gap-2">
        {!file && (
          <input
            type="file"
            ref={inputRef}
            {...props}
            className={cn(
              "block w-full text-sm text-slate-900 dark:text-white",
              "file:mr-4 file:py-2 file:px-4",
              "file:rounded-md file:border-0",
              "file:text-sm file:font-semibold",
              "file:bg-primary file:text-white",
              "hover:file:bg-primary/90",
              "file:transition-colors file:duration-200",
              "border-0 bg-white dark:bg-slate-900",
              "focus:outline-none focus:ring-2 focus:ring-primary transition-all duration-200",
              "disabled:opacity-50 disabled:cursor-not-allowed",
              className
            )}
            onChange={handleChange}
          />
        )}
        {file && (
          <div className="flex items-center gap-1 bg-slate-200 dark:bg-slate-800 px-3 py-2 rounded-md">
            <span className="truncate max-w-[8rem] text-xs text-slate-700  dark:text-slate-100">
              {file.name}
            </span>
            <button
              type="button"
              onClick={handleRemove}
              className="ml-2 px-2 py-1 rounded text-white text-xs font-semibold transition"
              title="Remove selected file"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={16}
                height={16}
                fill="none"
                viewBox="0 0 16 16"
                className="text-red-600 dark:text-red-400"
              >
                <path
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4l8 8M12 4l-8 8"
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    );
  }
);

FileInput.displayName = "FileInput";

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = "text", ...props }, ref) => {
    if (type === "file") {
      return <FileInput ref={ref} {...props} className={className} />;
    }

    return (
      <input
        type={type}
        ref={ref}
        {...props}
        className={cn(
          "w-full h-10 md:h-9 px-3 py-2 rounded-md",
          "border border-slate-300 bg-white dark:bg-slate-900 dark:border-slate-700",
          "text-sm text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500",
          "focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className
        )}
      />
    );
  }
);

Input.displayName = "Input";

export { Input };
